import { type Ref, forwardRef } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import getFieldError from '@/shared/form/helpers/getFieldError';
import useMapCustomValidators from '@/shared/form/hooks/useMapCustomValidators';
import type { InputProps } from '@/shared/form/types';

import { SwitchGroup as MantineSwitchGroup, type SwitchGroupProps as MantineSwitchGroupProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

export type SwitchGroupProps<T extends FieldValues> = InputProps<UseControllerProps<T>> &
  Omit<MantineSwitchGroupProps, 'value' | 'checked' | 'defaultValue'>;

export const SwitchGroup = forwardRef(function SwitchGroup<T extends FieldValues>(
  { name, control, defaultValue, shouldUnregister, onChange, ...props }: SwitchGroupProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const rules = useMapCustomValidators(props, true);
  const {
    field: { value, onChange: fieldOnChange, ref: insideRef, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const mergedRefs = useMergedRef(insideRef, ref);

  return (
    <MantineSwitchGroup
      ref={mergedRefs}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(name, e);
      }}
      error={getFieldError(fieldState)}
      mb="md"
      {...field}
      {...props}
    />
  );
});
